import { getNegotiationHistory, getHistoryPreviousMessages, updateNegotiationHistory } from 'Api/quoteNegotiationApi'
import { parseChatMessage, parseTemporaryMessage, parseSystemMessage } from 'Parsers/chatParser'
import { formatChatMessage } from 'Formatters/chatFormatter'
import unionBy from 'lodash/unionBy'
import orderBy from 'lodash/orderBy'
import uniqBy from 'lodash/uniqBy'
import cloneDeep from 'lodash/cloneDeep'
import messageStatus from 'Enums/messageStatus'
import historyOrigin from 'Enums/historyOrigin'
import { chatSignalR } from 'Services/signalR'
import { default as compareAscDate } from 'date-fns__legacy__/compare_asc'
import { default as parseDate } from 'date-fns__legacy__/parse'
import { HubConnection } from '@aspnet/signalr';
import { ChatHistoryMessage, ChatMessage, ApiChatHistoryMessage, ChatFile } from 'Types';
import CompanyChatState from 'Types/_Store/CompanyChat'
import chatMessageTypes from 'Enums/chatMessageTypes'
import { isCompletionStatement } from '@babel/types'

const state: CompanyChatState = {
  negotiationId: null,
  connectionId: null,
  inputChatFocused: false,

  requesting: {
    chatRealtime: false,
    companyList: false,
    companyStatus: false,
    lastMessages: false,
    newChat: false,
    previousMessages: false,
    sendMesssage: false
  },

  draft: {
    type: chatMessageTypes.message,
    message: '',
    files: [],
    budget: 0,
  },


  chatHistory: [],
  unsentMessages: [],
  currentPage: 1,
  totalCount: 0,
}

const mutations = {
  request_start(state, type) {
    state.requesting[type] = true
  },

  request_success(state, type) {
    state.requesting[type] = false
  },

  request_failure(state, type) {
    state.requesting[type] = false
  },

  set_negotiation_id(state, negotiationId) {
    state.negotiationId = negotiationId
  },

  set_chat_history(state, chatHistory) {
    state.chatHistory = chatHistory
  },

  reset_chat_history(state) {
    state.chatHistory.length = 0
  },

  set_unsent_messages(state, unsentMessages) {
    state.unsentMessages = unsentMessages
  },

  set_selected_chat(state, selectedChat) {
    Object.assign(state.selectedChat, selectedChat)
  },

  set_connection_id(state, connectionId) {
    state.connectionId = connectionId
  },

  set_draft_type(state, type) {
    state.draft.type = type
  },

  set_draft_message(state, message) {
    state.draft.message = message
  },

  set_draft_files(state, files) {
    state.draft.files = files
  },

  set_draft_budget(state, budget) {
    state.draft.budget = budget
  },
  set_chat_focus(state, focused) {
    state.inputChatFocused = focused
  },
  set_current_page(state, currentPage) {
    state.currentPage = currentPage
  },
  set_total_count(state, totalCount) {
    state.totalCount = totalCount
  }
}

const actions = {
  async openChatConnection({ commit }): Promise<HubConnection> {
    commit('request_start', 'chatRealtime')

    try {
      const connection = await chatSignalR()

      const connectionId = await connection.invoke('getConnectionId')
      commit('set_connection_id', connectionId)

      commit('request_success', 'chatRealtime')

      return connection
    } catch (err) {
      commit('request_failure', 'chatRealtime')

      throw err
    }
  },

  async closeChatConnection({ commit }, connection): Promise<HubConnection> {
    commit('request_start', 'chatRealtime')

    try {
      const result = await connection.stop()
      commit('set_connection_id', null)

      commit('request_success', 'chatRealtime')

      return result
    } catch (err) {
      commit('request_failure', 'chatRealtime')

      throw err
    }
  },

  updateChatHistory({ commit, state }, newMessages = []): ChatHistoryMessage[] {
    const { chatHistory } = state

    const isSendingMessages = chatHistory.some(msg => msg.status === messageStatus.sending)

    const mergedMessages = isSendingMessages
      ? unionBy(chatHistory, newMessages, 'id')
      : unionBy(newMessages, chatHistory, 'id')

    const uniqueMessages = uniqBy(mergedMessages, 'id')
    const newHistory = orderBy(uniqueMessages, 'id')

    commit('set_chat_history', newHistory)

    return newHistory
  },

  addWelcomeMessage({ state, rootState, dispatch }) {
    const { chatHistory, negotiationId } = state
    const { negotiationList } = rootState.quoteNegotiation

    const negotiation = negotiationList.find(item => item.negotiationId === negotiationId)
    const newMessageList = cloneDeep(chatHistory)

    const systemMessage = parseSystemMessage(`
      Parabéns! 😉 A sua empresa está negociando com ${negotiation.sender.name}.

      Aproveite para deixar uma mensagem e também anexar orçamentos.

      Ao informar dados no chat, lembre-se: o solicitante poderá compartilhar a cotação com outros intessados, como corpo diretivo, administradora ou em assembleias condominiais.
    `.trim())

    newMessageList.push(systemMessage)
    dispatch('updateChatHistory', newMessageList)

    return systemMessage
  },

  async getLastMessages({ commit, state, dispatch }, negotiationId: number) {
    commit('request_start', 'lastMessages')

    try {
      const { connectionId } = state
      const { data } = await getNegotiationHistory(negotiationId, connectionId)
      commit('set_negotiation_id', negotiationId)
      commit('set_total_count', data.totalCount)

      const { items: messageList = [] } = data
      const newMessages = messageList.map(parseChatMessage)

      dispatch('updateChatHistory', newMessages)

      if (newMessages.length === 0) {
        dispatch('addWelcomeMessage')
      }

      commit('request_success', 'lastMessages')

      return data
    } catch (err) {
      commit('request_failure', 'lastMessages')

      throw err
    }
  },

  async getPreviousMessages({ commit, state, dispatch }): Promise<ChatHistoryMessage[]> {
    commit('request_start', 'previousMessages')

    try {
      const { negotiationId, chatHistory } = state

      const todayDate = parseDate(Date.now())

      const dateList = chatHistory.map(item => item.datetime)
      const [ firstDate = todayDate ] = dateList.sort(compareAscDate)
      commit('set_current_page', state.currentPage + 1)

      const { data = [] } = await getHistoryPreviousMessages(negotiationId, state.currentPage)
      const newMessages = data.map(parseChatMessage)

      dispatch('updateChatHistory', newMessages)

      commit('request_success', 'previousMessages')

      return newMessages
    } catch (err) {
      commit('request_failure', 'previousMessages')

      throw err
    }
  },

  async sendMessage({ commit, dispatch, state }, message: ChatMessage = null) {
    const { negotiationId } = state

    const chatMessage = message || cloneDeep(state.draft)
    const temporaryMessage = await dispatch('addTemporaryMessage', chatMessage)

    dispatch('resetDraft')

    try {
      const { identifier } = temporaryMessage

      const apiChatMessage = formatChatMessage(chatMessage, negotiationId, identifier)
      const { data } = await updateNegotiationHistory(apiChatMessage)

      dispatch('updateMessage', data)

      return data
    } catch (err) {
      commit('set_unsent_messages', temporaryMessage.id)
      commit('request_failure', 'sendMesssage')

      throw err
    }
  },

  addTemporaryMessage({ commit, state, rootState }, chatMessage: ChatMessage) {
    const { user, company } = rootState.account

    const temporaryMessage = parseTemporaryMessage(
      chatMessage,
      user.name,
      company.name,
      historyOrigin.supplier
    )

    const newHistory = [
      ...cloneDeep(state.chatHistory),
      temporaryMessage
    ]

    commit('set_chat_history', newHistory)

    return temporaryMessage
  },

  addMessage({ commit, state }, apiMessage: ApiChatHistoryMessage) {
    const newMessage = parseChatMessage(apiMessage)

    const newHistory = [
      ...cloneDeep(state.chatHistory),
      newMessage
    ]

    commit('set_chat_history', newHistory)

    return newMessage
  },

  updateMessage({ commit, state }, apiMessage: ApiChatHistoryMessage) {
    const previousHistory = cloneDeep(state.chatHistory)

    const newMessage = parseChatMessage(apiMessage)

    const newHistory = previousHistory.map(item =>
      item.identifier === newMessage.identifier ? newMessage : item
    )

    commit('set_chat_history', newHistory)

    return newMessage
  },

  updateDraftType({ commit }, type: number) {
    commit('set_draft_type', type)
  },

  updateDraftMessage({ commit }, message: string) {
    commit('set_draft_message', message)
  },

  updateDraftFiles({ commit }, files: ChatFile[]) {
    commit('set_draft_files', files)
  },

  updateDraftBudget({ commit }, budget: number) {
    commit('set_draft_budget', budget)
  },

  removeDraftFileByName({ commit, state }, filename) {
    const fileList = state.draft.files.filter(item => item.name !== filename)

    commit('set_draft_files', fileList)
  },

  resetDraft({ commit }) {
    commit('set_draft_message', '')
    commit('set_draft_files', [])
    commit('set_draft_budget', 0)
    commit('set_draft_type', chatMessageTypes.message)
  },

  markMessageRead({ commit, state }) {
    const newHistory = cloneDeep(state.chatHistory)

    const msgNotRead = newHistory.filter(item => item.status !== messageStatus.read)
    msgNotRead.forEach(item => item.status = messageStatus.read)

    commit('set_chat_history', newHistory)

    return msgNotRead
  },

  closeChat({ commit }) {
    commit('reset_chat_history')
  },

  setChatFocused({ commit }, focused){
    commit('set_chat_focus', focused)
  }
}

export default {
  namespaced: true,

  state,
  mutations,
  actions,
}